"use client";

import classNames from "classnames";
import Cookies from "js-cookie";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { APRIL_FOOLS_SOLUTION } from "./aprilFools";
import { COOKIE_KEY, CookieState } from "./cookies";
import { formatTimeInSeconds } from "./formatTimeInSeconds";
import { getSolveTimeEmoji } from "./getSolveTimeEmoji";
import { getWordleFailureReason } from "./getWordleFailureReason";
import { Header } from "./Header";
import { InfoPopup } from "./InfoPopup";
import { Keyboard } from "./Keyboard";
import { WordleInTwo } from "./types";
import { useSolveClock } from "./useSolveClock";
import { WordleGuess } from "./WordleGuess";

export declare namespace WordleIn2Game {
  export interface Props {
    puzzleIndex: number;
    puzzle: WordleInTwo;
    initialGuess: string | undefined;
    doNotUseCookie?: boolean;
    yesterdaysAnswer: string;
    initialSolveTime?: number;
    isAprilFools?: boolean;
  }
}

const ALPHA_REGEX = /^[a-zA-Z]$/;

interface State {
  currentGuess: string;
  didWin: boolean;
  shouldShakeGuess: boolean;
}

export function WordleIn2Game({
  puzzle,
  puzzleIndex,
  initialGuess = "",
  yesterdaysAnswer,
  doNotUseCookie = false,
  initialSolveTime,
  isAprilFools = false,
}: WordleIn2Game.Props) {
  const posthog = usePostHog();

  const solution = isAprilFools ? APRIL_FOOLS_SOLUTION : puzzle.solution;

  const isCorrectSolution = useCallback(
    (guess: string) => {
      return guess.toLowerCase() === solution.toLowerCase();
    },
    [solution]
  );

  const [{ currentGuess, didWin, shouldShakeGuess }, setState] =
    useState<State>({
      currentGuess: initialGuess,
      didWin: isCorrectSolution(initialGuess),
      shouldShakeGuess: false,
    });

  const { timeInSeconds } = useSolveClock({
    hasSolved: didWin,
    initialTimeInSeconds: initialSolveTime ?? 0,
  });

  useEffect(() => {
    if (!doNotUseCookie) {
      setCookieState({ puzzleIndex, currentGuess, time: timeInSeconds });
    }
  }, [currentGuess, doNotUseCookie, puzzleIndex, timeInSeconds]);

  const canAddLetter = currentGuess.length < 5;

  const onChangeCurrentGuess = useCallback(
    (updater: (oldGuess: string) => string) => {
      setState((existing) => ({
        ...existing,
        currentGuess: updater(existing.currentGuess),
      }));
    },
    []
  );

  useEffect(() => {
    if (!didWin && isCorrectSolution(currentGuess)) {
      posthog.capture("solved", {
        puzzleIndex,
        solution,
        timeInSeconds,
      });
      setState((existing) => ({
        ...existing,
        didWin: true,
        shouldShakeGuess: false,
      }));
    }
  }, [
    currentGuess,
    didWin,
    isCorrectSolution,
    posthog,
    puzzleIndex,
    solution,
    timeInSeconds,
  ]);

  const onPressLetter = useCallback(
    (letter: string) => {
      onChangeCurrentGuess((oldGuess) => oldGuess + letter);
    },
    [onChangeCurrentGuess]
  );

  const onPressBackspace = useCallback(() => {
    failureReasonTimeout.current = undefined;
    shouldShakeTimeout.current = undefined;
    setShowFailureReason(false);
    setState((existing) => ({ ...existing, shouldShakeGuess: false }));

    onChangeCurrentGuess((oldGuess) => oldGuess.slice(0, oldGuess.length - 1));
  }, [onChangeCurrentGuess]);

  const shouldShakeTimeout = useRef<NodeJS.Timeout>(undefined);
  const [shouldShowFailureReason, setShowFailureReason] = useState(false);
  const failureReasonTimeout = useRef<NodeJS.Timeout>(undefined);
  const [failureReason, setFailureReason] = useState("");

  const commitGuess = useCallback(() => {
    if (shouldShakeTimeout.current != null) {
      clearTimeout(shouldShakeTimeout.current);
    }
    setState((existing) => ({ ...existing, shouldShakeGuess: true }));
    shouldShakeTimeout.current = setTimeout(() => {
      setState((existing) => ({ ...existing, shouldShakeGuess: false }));
    }, 750);

    const failureReason = isAprilFools
      ? "Nope"
      : getWordleFailureReason({
          guess1: puzzle.guess1,
          result: puzzle.result,
          guess2: currentGuess,
        });
    if (failureReason != null) {
      setFailureReason(failureReason);
      setShowFailureReason(true);
      if (failureReasonTimeout.current != null) {
        clearTimeout(failureReasonTimeout.current);
      }
      failureReasonTimeout.current = setTimeout(() => {
        setShowFailureReason(false);
      }, 2_000);
    }
  }, [currentGuess, isAprilFools, puzzle.guess1, puzzle.result]);

  useEffect(() => {
    if (didWin) {
      return;
    }

    const keyboardListener = (event: KeyboardEvent) => {
      const key = event.key;
      if (canAddLetter && !event.metaKey && ALPHA_REGEX.test(key)) {
        onPressLetter(key);
      }
      if (key === "Backspace") {
        onPressBackspace();
      }
      if (key === "Enter" && currentGuess.length === 5) {
        commitGuess();
      }
    };

    document.addEventListener("keydown", keyboardListener);
    return () => {
      document.removeEventListener("keydown", keyboardListener);
    };
  }, [
    canAddLetter,
    commitGuess,
    currentGuess.length,
    didWin,
    onPressBackspace,
    onPressLetter,
  ]);

  const shouldCopyPaste = useMemo(() => !isMobile(), []);

  const onClickShare = async () => {
    const { minutes, seconds } = formatTimeInSeconds(timeInSeconds);

    let text = isAprilFools
      ? "Word in 1\n"
      : `Word in 2 #${puzzleIndex + 1}\n\n` +
        convertResultToWordleSquares(puzzle.result);

    text +=
      "\n🟩🟩🟩🟩🟩\n" +
      getSolveTimeEmoji(timeInSeconds) +
      " " +
      `${minutes}:${seconds}` +
      "\n\nhttps://wordin2.com";

    posthog.capture("shared");

    if (typeof navigator === "undefined") {
      return;
    }

    if (shouldCopyPaste) {
      navigator.clipboard.writeText(text);
      alert("Copied to clipboard");
    } else if (navigator.share != null) {
      await navigator.share({
        title: "Word in 2",
        text,
      });
    }
  };

  const letterToColor = useMemo(() => {
    if (isAprilFools) {
      return {};
    }
    return [...new Set(puzzle.guess1)].reduce<Keyboard.LetterToColorMap>(
      (acc, letter) => {
        const colors = getColorsForLetter({
          letter,
          guess: puzzle.guess1,
          result: puzzle.result,
        });
        const dominantColor = getDominantColor(colors);
        if (dominantColor != null) {
          acc[letter.toUpperCase()] = dominantColor;
        }
        return acc;
      },
      {}
    );
  }, [isAprilFools, puzzle.guess1, puzzle.result]);

  const [isShowingInfoPopup, setIsShowingInfoPopup] = useState(false);

  return (
    <div className="flex flex-col flex-1 relative">
      {isShowingInfoPopup && (
        <InfoPopup
          close={() => {
            setIsShowingInfoPopup(false);
          }}
          yesterdaysAnswer={yesterdaysAnswer}
        />
      )}
      <Header
        onClickInfo={() => {
          setIsShowingInfoPopup(true);
        }}
        timerInSeconds={timeInSeconds}
      />
      <div className="flex flex-col items-center">
        <div className="text-4xl font-bold font-serif">
          {isAprilFools ? "Word in 1" : "Word in 2"}
        </div>
        <div className="mt-5 mb-10">
          {isAprilFools
            ? "Can you guess the word?"
            : "Can you guess the only possible solution?"}
        </div>
      </div>
      <div className="flex flex-col max-w-[500px] mx-auto w-full flex-1">
        <div className="flex flex-col flex-1">
          <div className="flex flex-col gap-2">
            {!isAprilFools && (
              <WordleGuess
                word={puzzle.guess1}
                result={puzzle.result}
                colorBorderOnly
              />
            )}
            <WordleGuess
              word={currentGuess}
              result={didWin ? "GGGGG" : undefined}
              shake={shouldShakeGuess}
              colorBorderOnly={false}
            />
          </div>

          <div className="flex flex-1 min-w-0 px-2 mb-5">
            {didWin ? (
              <div className="flex flex-1 justify-center items-center flex-col gap-4">
                <div className="text-3xl font-serif">Well done!</div>
                <button
                  className="m-3 bg-[rgb(97,140,85)] hover:bg-[rgb(86,124,75)] active:bg-[rgb(75,108,66)] px-10 py-4 rounded text-2xl select-none cursor-pointer font-extrabold"
                  onClick={onClickShare}
                >
                  SHARE
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                <div className="flex flex-1 justify-center items-center">
                  <div
                    className={classNames(
                      "bg-gray-400/15 rounded py-1 px-2 transition",
                      shouldShowFailureReason ? "opacity-100" : "opacity-0"
                    )}
                  >
                    {failureReason}
                  </div>
                </div>
                <Keyboard
                  onPressBackspace={
                    currentGuess.length > 0 ? onPressBackspace : undefined
                  }
                  onPressCharacter={canAddLetter ? onPressLetter : undefined}
                  onPressEnter={
                    currentGuess.length === 5 ? commitGuess : undefined
                  }
                  letterToColor={letterToColor}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function setCookieState(state: CookieState) {
  Cookies.set(COOKIE_KEY, JSON.stringify(state), {
    // 2 days
    expires: 2,
  });
}

const MOBILE_USER_AGENT_REGEX =
  /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone|Opera Mini|IEMobile/i;
function isMobile() {
  return (
    typeof navigator !== "undefined" &&
    MOBILE_USER_AGENT_REGEX.test(navigator.userAgent)
  );
}

function convertResultToWordleSquares(result: string): string {
  return result.split("").map(convertCharResultToWordleSquare).join("");
}

function convertCharResultToWordleSquare(charResult: string): string {
  switch (charResult) {
    case "G":
      return "🟩";
    case "B":
      return "⬛";
    case "Y":
      return "🟨";
    default:
      return "";
  }
}

function getColorsForLetter({
  guess,
  result,
  letter,
}: {
  guess: string;
  result: string;
  letter: string;
}) {
  const colors = new Set<string>();
  for (const [i, guessLetter] of [...guess].entries()) {
    if (guessLetter === letter) {
      colors.add(result[i]);
    }
  }
  return colors;
}

function getDominantColor(colors: Set<string>) {
  if (colors.has("G")) {
    return "green";
  }
  if (colors.has("Y")) {
    return "yellow";
  }
  if (colors.has("B")) {
    return "faded-black";
  }
}
